import { AnnotationLayer, type PageViewport, type PDFPageProxy } from 'pdfjs-dist';
import { memo, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type IPDFLinkService } from '../utils/IPDFLinkService';

type PDFPageAnnotationLayerProps = {
  pdfPage: PDFPageProxy;
  pageViewport: PageViewport;
  linkService: IPDFLinkService;
};

export const PDFPageAnnotationLayer = memo(({ pdfPage, linkService, pageViewport }: PDFPageAnnotationLayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const annotationLayerRef = useRef<AnnotationLayer | null>();
  const linkServiceRef = useRef(linkService);

  const isMounted = useRef(false);

  useIsomorphicLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    const hide = () => {
      if (containerRef.current) {
        containerRef.current.hidden = true;
      }
    };

    const show = () => {
      if (containerRef.current) {
        containerRef.current.hidden = false;
      }
    };

    const renderAnnotations = async () => {
      const linkServiceChanged = linkServiceRef.current !== linkService;
      linkServiceRef.current = linkService;

      if (annotationLayerRef.current && !linkServiceChanged) {
        // @ts-expect-error - incorrect type in update method
        annotationLayerRef.current.update({
          viewport: pageViewport.clone({ dontFlip: true }),
        });

        return;
      }

      if (linkServiceChanged && containerRef.current) {
        containerRef.current.replaceChildren();
        annotationLayerRef.current = null;
      }

      const annotations = await pdfPage.getAnnotations();

      if (annotations.length === 0) {
        hide();
        return;
      }

      annotationLayerRef.current = new AnnotationLayer({
        accessibilityManager: null,
        annotationCanvasMap: null,
        annotationEditorUIManager: null,
        div: containerRef.current,
        page: pdfPage,
        structTreeLayer: null,
        viewport: pageViewport.clone({ dontFlip: true }),
      });

      if (!containerRef.current) {
        return;
      }

      await annotationLayerRef.current.render({
        annotations,
        div: containerRef.current,
        linkService: linkService,
        page: pdfPage,
        renderForms: false,
        viewport: pageViewport.clone({ dontFlip: true }),
      });

      if (!isMounted.current) {
        return;
      }

      show();
    };

    void renderAnnotations();
  }, [linkService, pageViewport, pdfPage]);

  return <div className="pdfAnnotationLayer" ref={containerRef} />;
});

PDFPageAnnotationLayer.displayName = 'PDFPageAnnotationLayer';
